import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Plane, Clock, ArrowLeft, Check, Phone, Train, Building, Landmark, Briefcase, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { GatwickLiveWait } from "@/components/GatwickLiveWait";
import { AREAS, COMPANY, BOOKING_URL } from "@/data/site";
import areaContentData from "@/data/area-content.json";

const contentBySlug = Object.fromEntries(areaContentData.map((a) => [a.slug, a]));

const paragraphs = (text = "") =>
  text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/" replace />;
  const c = contentBySlug[slug] || {};
  const meta = c.meta || {};
  const hero = c.hero || {};
  const entities = c.entities || {};
  const faqs = c.faqs || [];
  const sections = c.sections || [];
  const internalLinks = c.internal_links || [];

  // JSON-LD schemas
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pagePath = `/areas/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${origin}${pagePath}#localbusiness`,
      name: `${COMPANY.name} — ${area.name}`,
      description: `Licensed private hire and airport transfers to and from ${area.name}, serving Gatwick, Heathrow and London. Fixed fares, DBS-checked chauffeurs, 24/7.`,
      url: `${origin}${pagePath}`,
      telephone: COMPANY.phoneRaw,
      email: COMPANY.email,
      image: `${origin}${area.image}`,
      priceRange: "££",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Churchill Court",
        addressLocality: "Crawley",
        postalCode: "RH10 9KU",
        addressCountry: "GB",
      },
      areaServed: [
        { "@type": "Place", name: area.name },
        { "@type": "Place", name: "Gatwick Airport" },
        { "@type": "Place", name: "Surrey" },
        { "@type": "Place", name: "West Sussex" },
      ],
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
      }],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: String(COMPANY.reviews || "774"),
        bestRating: "5",
        worstRating: "1",
      },
      sameAs: [
        "https://www.google.com/search?q=Clocktower+Cars+Gatwick",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "TaxiService",
      name: `Clocktower Cars Gatwick — ${area.name} taxis`,
      provider: { "@id": `${origin}${pagePath}#localbusiness` },
      areaServed: { "@type": "Place", name: area.name },
      url: `${origin}${pagePath}`,
      priceRange: "££",
    },
    faqs.length > 0 && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Areas", item: `${origin}/#areas` },
        { "@type": "ListItem", position: 3, name: area.name, item: `${origin}${pagePath}` },
      ],
    },
  ].filter(Boolean);

  const entityGroups = [
    { key: "stations", label: "Nearby rail stations", Icon: Train, items: entities.stations },
    { key: "hotels", label: "Hotels near " + area.name, Icon: Building, items: entities.hotels },
    { key: "attractions", label: "Attractions & landmarks", Icon: Landmark, items: entities.attractions },
    { key: "businesses", label: "Business & healthcare", Icon: Briefcase, items: entities.businesses },
  ].filter((g) => Array.isArray(g.items) && g.items.length > 0);

  return (
    <Layout>
      <Seo
        title={meta.title || `${area.name} Taxis`}
        description={meta.description}
        path={pagePath}
        image={area.image}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative py-20 md:py-24">
        <div className="absolute inset-0">
          <img src={area.image} alt={`${area.name} — Clocktower Cars taxi coverage`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
            <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-slate-300 hover:text-brand-light transition-colors mb-6 text-sm">
              <ArrowLeft className="h-4 w-4" /> Back home
            </Link>
            <p className="text-brand-light font-semibold uppercase tracking-[0.2em] text-xs mb-4">{hero.eyebrow || `Taxis in ${area.name}`}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] max-w-4xl">
              {hero.h1 || area.tagline}
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">{hero.intro || area.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={BOOKING_URL} data-testid="book-cta-hero" className="bg-brand hover:bg-brand-dark text-white px-7 py-4 rounded-lg font-semibold transition-colors active:scale-[0.98]">
                Book a {area.name} taxi
              </a>
              <a href={`tel:${COMPANY.phoneRaw}`} data-testid="call-cta-hero" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-7 py-4 rounded-lg font-medium transition-colors flex items-center gap-2">
                <Phone className="h-4 w-4 text-brand-light" /> {COMPANY.phone}
              </a>
            </div>
        </div>
      </section>

      {/* OVERVIEW + BOOKING */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-10">
            {c.overview && (
              <div data-testid="area-overview" className="prose max-w-none prose-slate">
                {paragraphs(c.overview).map((p, i) => (
                  <p key={i} className="text-slate-600 text-lg leading-relaxed mb-5">{p}</p>
                ))}
              </div>
            )}

            {/* AIRPORT FARES */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Airport transfers from {area.name}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {area.airports.map((ap) => (
                  <div key={ap.name} data-testid={`airport-${ap.name.toLowerCase()}`} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <Plane className="h-5 w-5 text-brand" strokeWidth={1.75} />
                      <span className="font-heading text-xl font-bold text-slate-900">{ap.name}</span>
                    </div>
                    <p className="text-sm text-slate-500 flex items-center gap-2 mb-1"><Clock className="h-4 w-4" /> Approx {ap.time}</p>
                    <p className="text-brand text-lg font-bold">From {ap.price}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Popular destinations */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Popular {area.name} destinations</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {area.landmarks.map((lm) => (
                  <li key={lm} className="flex items-center gap-3 text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                    <MapPin className="h-4 w-4 text-brand shrink-0" /> {lm}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-5">
              <div data-testid="area-book-card" className="bg-white rounded-2xl p-7 md:p-8 shadow-[0_24px_50px_-12px_rgba(0,0,0,0.15)] border border-[#EAEBEA]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="h-2 w-2 rounded-full bg-brand animate-pulse" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand">Instant online booking</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-ink mb-2">Book a {area.name} taxi</h3>
                <p className="text-ink/60 leading-relaxed mb-6 text-sm">
                  Fixed fares, live pricing and instant confirmation via our secure online booker — trusted by thousands of {area.name} travellers each year.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {area.airports.slice(0, 4).map((ap) => (
                    <div key={ap.name} className="rounded-xl bg-surface p-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink/40">{ap.name}</p>
                      <p className="font-heading text-lg font-black text-ink mt-1">{ap.price}</p>
                      <p className="text-[10px] text-ink/50 mt-0.5">{ap.time}</p>
                    </div>
                  ))}
                </div>

                <a
                  href={BOOKING_URL}
                  data-testid="area-book-online-btn"
                  className="block w-full text-center py-4 h-auto bg-brand hover:bg-brand-light text-white rounded-full text-base font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.35)] active:scale-[0.98]"
                >
                  Book online now
                </a>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="mt-3 flex items-center justify-center gap-2 w-full py-3.5 rounded-full border-2 border-ink/10 hover:border-ink text-ink font-bold transition-colors text-sm"
                >
                  <Phone className="h-4 w-4 text-brand" strokeWidth={2.25} /> Or call {COMPANY.phone}
                </a>

                <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-ink/40 font-medium">
                  <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand" strokeWidth={3} /> No payment now</span>
                  <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand" strokeWidth={3} /> Fixed price</span>
                </div>
              </div>

              <GatwickLiveWait variant="light" area={area.name} />
            </div>
          </div>
        </div>
      </section>

      {/* DEEP SECTIONS */}
      {sections.length > 0 && (
        <section className="py-16 md:py-20 bg-surface">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-12">
            {sections.map((s, i) => (
              <motion.article
                key={s.heading}
                data-testid={`section-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              >
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4 tracking-tight">
                  {s.heading}
                </h2>
                {paragraphs(s.body).map((p, j) => (
                  <p key={j} className="text-slate-600 leading-relaxed mb-4 text-[17px]">{p}</p>
                ))}
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* ENTITY LISTS — stations, hotels, attractions, businesses */}
      {entityGroups.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Places we regularly serve near {area.name}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {entityGroups.map((g) => (
                <div key={g.key} data-testid={`entity-${g.key}`} className="bg-surface border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-4 text-brand">
                    <g.Icon className="h-5 w-5" strokeWidth={1.75} />
                    <p className="text-xs font-bold uppercase tracking-[0.15em]">{g.label}</p>
                  </div>
                  <ul className="space-y-2 text-slate-700 text-sm">
                    {g.items.map((it) => (
                      <li key={it} className="flex gap-2"><span className="text-brand">•</span> {it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY US */}
      <section className="py-16 bg-surface">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-sm">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Why {area.name} chooses Clocktower Cars</h3>
            <ul className="grid sm:grid-cols-2 gap-4">
              {[
                "Local drivers who know every shortcut and postcode",
                "Fixed, upfront fares with no surge pricing",
                "24/7 availability — early flights and late arrivals",
                "Immaculate all-black Mercedes fleet, spotlessly valeted",
                "DBS-checked, fully licensed private hire drivers",
                "Free flight tracking and meet-and-greet at Gatwick",
              ].map((r) => (
                <li key={r} className="flex items-start gap-3 text-slate-600"><Check className="h-5 w-5 text-brand shrink-0 mt-0.5" /> {r}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-brand">FAQs</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-8 tracking-tight">Questions {area.name} travellers ask</h2>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="bg-surface rounded-xl border border-slate-200 px-5">
                  <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16 bg-ink">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Ready to travel from {area.name}?
          </h2>
          {c.cta && <p className="text-white/70 leading-relaxed max-w-2xl mx-auto mb-8">{c.cta}</p>}
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={BOOKING_URL} data-testid="book-cta-final" className="bg-brand hover:bg-brand-dark text-white px-8 py-4 rounded-full font-bold transition-colors active:scale-[0.98]">
              Book now
            </a>
            <a href={`tel:${COMPANY.phoneRaw}`} className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-full font-bold transition-colors flex items-center gap-2">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* RELATED AREAS */}
      <section className="py-16 bg-surface border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {internalLinks.length > 0 && (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">You might also need</p>
              <div className="flex flex-wrap gap-3 mb-10">
                {internalLinks.map((l) => (
                  <Link key={l.slug} to={`/areas/${l.slug}`} data-testid={`related-${l.slug}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-brand text-slate-700 hover:text-brand px-5 py-2.5 rounded-full transition-colors">
                    {l.label} taxis <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ))}
              </div>
            </>
          )}
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">Every area we cover</p>
          <div className="flex flex-wrap gap-2.5">
            {AREAS.filter((a) => a.slug !== slug).map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="bg-white border border-slate-200 hover:border-brand text-slate-600 hover:text-brand px-4 py-2 rounded-lg transition-colors text-sm">
                {a.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
